import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import api from '../utils/api';

interface SavePaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  client: {
    id: string;
    name: string;
  };
}

interface SetupIntentResponse {
  clientSecret: string;
  stripeAccountId: string;
}

// Inner form component that uses Stripe hooks
const SetupForm: React.FC<{
  clientId: string;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ clientId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'An error occurred');
        setProcessing(false);
        return;
      }

      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (setupError) {
        setError(setupError.message || 'Failed to save card');
        setProcessing(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Directly save the payment method (fallback for webhooks)
        try {
          await api.post(`/clients/${clientId}/payment-methods/confirm`, {
            setupIntentId: setupIntent.id,
          });
        } catch (confirmErr) {
          // Confirm endpoint may return error if already saved via webhook - safe to ignore
        }
        onSuccess();
      } else {
        setError('Card could not be saved. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Setup error:', err);
      setError('An unexpected error occurred');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Enter card or bank account details to save for future payments. You will not be charged now.
        </Typography>
      </Box>

      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: {
            applePay: 'never',
            googlePay: 'never',
          },
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <DialogActions sx={{ mt: 3, px: 0 }}>
        <Button onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing}
        >
          {processing ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </form>
  );
};

const SavePaymentMethodDialog: React.FC<SavePaymentMethodDialogProps> = ({
  open,
  onClose,
  onSuccess,
  client,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<SetupIntentResponse | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize setup when dialog opens
  const initializeSetup = useCallback(async () => {
    if (!open || !client.id) return;

    setLoading(true);
    setError(null);
    setSetupData(null);
    setSaveSuccess(false);

    try {
      // Create SetupIntent
      const response = await api.post(`/clients/${client.id}/payment-methods/setup`);
      const data: SetupIntentResponse = response.data;

      setSetupData(data);

      // Load Stripe with the connected account
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!publishableKey) {
        throw new Error('Stripe publishable key not configured');
      }

      const stripeInstance = loadStripe(publishableKey, {
        stripeAccount: data.stripeAccountId,
      });
      setStripePromise(stripeInstance);
    } catch (err: any) {
      console.error('Failed to initialize setup:', err);
      setError(
        err.response?.data?.message || 'Failed to initialize. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [open, client.id]);

  useEffect(() => {
    initializeSetup();
  }, [initializeSetup]);

  const handleSuccess = () => {
    setSaveSuccess(true);
    // Give webhook time to process before closing and refreshing
    // This ensures the payment method appears in the list
    setTimeout(() => {
      onSuccess();
      onClose();
    }, 2500);
  };

  const handleClose = () => {
    if (!loading) {
      setSetupData(null);
      setStripePromise(null);
      setError(null);
      setSaveSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        Add Payment Method
        <Typography variant="body2" color="text.secondary">
          {client.name}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {saveSuccess ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
            }}
          >
            <Alert severity="success" sx={{ mb: 2 }}>
              Payment method saved successfully!
            </Alert>
            <Typography color="text.secondary">
              It is now available for future payments.
            </Typography>
          </Box>
        ) : loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Initializing...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button variant="contained" onClick={initializeSetup}>
                Try Again
              </Button>
            </Box>
          </Box>
        ) : setupData && stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: setupData.clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#C9772B',
                  fontFamily: 'Roboto, sans-serif',
                },
              },
              // Disable Link (Stripe's "save for faster checkout" feature)
              loader: 'auto',
            }}
          >
            <SetupForm
              clientId={client.id}
              onSuccess={handleSuccess}
              onCancel={handleClose}
            />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default SavePaymentMethodDialog;
